import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Plus, ArrowUpRight } from "lucide-react";
import { FadeIn, MaskedLines, Eyebrow } from "@/components/Reveal";
import { services } from "@/data/services";
import { useTitle } from "@/hooks/use-title";

const ServiceCard = ({ service, open, onToggle, position }) => {
  const Icon = service.icon;
  return (
    <FadeIn
      delay={position * 0.05}
      y={18}
      className={`overflow-hidden rounded-3xl border bg-white transition-colors duration-300 ${
        open ? "border-coral-300" : "border-slate-200 hover:border-coral-200"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`service-toggle-${service.id}`}
        aria-expanded={open}
        className="flex w-full items-center gap-5 px-6 py-6 text-left sm:gap-8 sm:px-9 sm:py-8"
      >
        <span className="hidden font-mono text-xs tracking-[0.2em] text-coral-500 sm:block">
          {service.index}
        </span>
        <span className="flex-1">
          <span className="block font-serif text-xl text-slate-900 sm:text-2xl">
            {service.title}
          </span>
          <span className="mt-1.5 block text-sm leading-relaxed text-slate-500">
            {service.blurb}
          </span>
        </span>
        <span className="hidden text-slate-300 sm:block">
          <Icon className="h-6 w-6" strokeWidth={1.5} />
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
            open
              ? "border-coral-500 bg-coral-500 text-white"
              : "border-slate-200 text-slate-500"
          }`}
        >
          <Plus className="h-4 w-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-100 px-6 pb-9 pt-7 sm:px-9">
              <p className="max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
                {service.description}
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid={`service-subs-${service.id}`}>
                {service.subs.map((sub) => (
                  <span
                    key={sub}
                    className="rounded-full border border-coral-200 bg-coral-50 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-coral-700"
                  >
                    {sub}
                  </span>
                ))}
              </div>
              <Link
                to={`/contact?service=${encodeURIComponent(service.title)}`}
                data-testid={`service-request-${service.id}`}
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-coral-600 transition-colors hover:text-coral-700"
              >
                Discuss this service
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </FadeIn>
  );
};

export default function Services() {
  useTitle("Services — THREENNOVATORS LLP");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(services[0].id);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        s.subs.some((sub) => sub.toLowerCase().includes(q))
    );
  }, [query]);

  const searching = query.trim().length > 0;

  return (
    <div data-testid="services-page">
      <section className="mx-auto max-w-7xl px-4 pb-14 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <FadeIn y={12}>
          <Eyebrow>Services</Eyebrow>
        </FadeIn>
        <h1
          className="mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          data-testid="services-headline"
        >
          <MaskedLines
            lines={[
              "Six domains.",
              <>
                One standard:{" "}
                <span className="italic text-coral-500">rigour.</span>
              </>,
            ]}
          />
        </h1>
        <FadeIn delay={0.45}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            From research articles and systematic reviews to SAR / MoA analysis
            and regulatory documentation — explore each domain and its
            specialised deliverables below.
          </p>
        </FadeIn>

        <FadeIn delay={0.55} className="mt-10 max-w-xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a deliverable — e.g. meta-analysis, thesis, RWE"
              data-testid="services-search-input"
              className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-12 pr-5 text-sm text-slate-900 outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-coral-400 focus:ring-2 focus:ring-coral-100"
            />
          </div>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="space-y-5" data-testid="services-directory">
          {visible.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              position={i}
              open={searching ? true : openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            />
          ))}
          {visible.length === 0 && (
            <FadeIn>
              <div
                className="rounded-3xl border border-dashed border-slate-300 bg-white px-8 py-16 text-center"
                data-testid="services-empty-state"
              >
                <p className="font-serif text-xl text-slate-900">
                  No exact match — but we likely still cover it.
                </p>
                <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
                  Our work is customized per project. Tell us what you need and
                  we will scope it together.
                </p>
                <Link
                  to="/contact"
                  data-testid="services-empty-contact-link"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-coral-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-coral-600"
                >
                  Get in Touch
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </FadeIn>
          )}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 py-16 sm:px-6 sm:py-20 lg:flex-row lg:items-center lg:px-8">
          <FadeIn>
            <h2 className="max-w-xl font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">
              Not sure which domain fits?{" "}
              <span className="italic text-coral-500">We will map it for you.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Link
              to="/contact"
              data-testid="services-cta-discuss-project"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-950 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-500"
            >
              Discuss Your Project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
